import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useUrlToken } from '@hooks/useUrlToken';
import { AxiosError } from 'axios';
import { useToast } from '@centraoneplus/ui';
import { authService } from '@services/auth.service';
import { useAuthStore } from '@stores/authStore';
import { PasswordInput } from '@components/PasswordInput';
import { newPasswordFormSchema, PASSWORD_REQUIREMENTS_MESSAGE } from '@/lib/validation';
import { AuthLinkProblem, BACK_TO_SIGN_IN } from '@components/auth/AuthLinkProblem';

interface ApiError {
  code: string;
  message: string;
  details?: Record<string, unknown>;
}

export default function ResetPasswordPage() {
  const token = useUrlToken();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const logout = useAuthStore((s) => s.logout);
  const toast = useToast();

  const validatePassword = (): boolean => {
    // Zod is the single source of truth for the password policy + match check.
    const result = newPasswordFormSchema.safeParse({ password, confirmPassword });
    if (!result.success) {
      setError(result.error.issues[0]?.message ?? PASSWORD_REQUIREMENTS_MESSAGE);
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!validatePassword()) {
      return;
    }

    setLoading(true);

    try {
      await authService.resetPassword(token, password);
      // The reset ends the account's sessions on the server. If someone is
      // signed in in this browser, sign out here too, or the login page would
      // send them straight back to the dashboard.
      if (isAuthenticated) await logout();
      toast.success('Password reset', 'You can now sign in with your new password.');
      navigate('/login');
    } catch (err) {
      const axiosError = err as AxiosError<{ error: ApiError }>;

      if (axiosError.response?.status === 400) {
        const code = axiosError.response.data?.error?.code;
        if (code === 'TOKEN_EXPIRED') {
          setError('This reset link has expired. Please request a new one.');
        } else if (code === 'TOKEN_INVALID' || code === 'TOKEN_ALREADY_USED') {
          setError('This reset link is invalid or has already been used.');
        } else {
          setError(axiosError.response.data?.error?.message || 'Invalid request');
        }
      } else if (axiosError.response?.data?.error?.message) {
        setError(axiosError.response.data.error.message);
      } else {
        setError('An error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <AuthLinkProblem
        title="This reset link doesn't work"
        message="This password reset link is invalid or missing. Please request a new one."
        action={{ to: '/forgot-password', label: 'Request new link' }}
        secondary={{ to: '/login', label: BACK_TO_SIGN_IN }}
      />
    );
  }

  return (
    <div>
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Set a new password</h2>
        <p className="mt-2 text-gray-600 dark:text-gray-400">Enter and confirm your new password below.</p>
      </div>

      <form className="space-y-6" onSubmit={handleSubmit}>
        {error && (
          <div className="px-4 py-3 rounded bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300">
            {error}
          </div>
        )}

        <div>
          <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            New password
          </label>
          <PasswordInput
            id="password"
            name="password"
            autoComplete="new-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="input dark:bg-gray-800 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
            wrapperClassName="mt-1"
            placeholder="Enter a strong password"
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            At least 12 characters with uppercase, lowercase, number, and special character
          </p>
        </div>

        <div>
          <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Confirm new password
          </label>
          <PasswordInput
            id="confirmPassword"
            name="confirmPassword"
            autoComplete="new-password"
            required
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className="input dark:bg-gray-800 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
            wrapperClassName="mt-1"
            placeholder="Confirm your password"
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Resetting...' : 'Reset password'}
        </button>
      </form>

      <p className="mt-6 text-center text-sm text-gray-600 dark:text-gray-400">
        <Link to="/login" className="text-primary-600 dark:text-primary-400 hover:text-primary-500 font-medium">
          Back to sign in
        </Link>
      </p>
    </div>
  );
}
